// The notification bell (ROADMAP #4; slot API agreed with Support 2026-10-31, D-084).
// A SHELL control in the header, before the theme toggle, in every module: a notice
// follows the person, not the active module. Modules feed it server-side only.
//
// Eager on purpose, and small: a plain <button> and the unread-count poll. The panel (the
// list, React Aria's popover) is a lazy chunk loaded on first open; the shell may not
// import React Aria or the kit statically (test/web/shell-kit-imports.test.js).
//
// No feed (bell-logic.js isGoneError: the API's 404, or a product gate before the server half
// exists) means the bell renders nothing and stops polling until reload. Nothing shows until
// the first count arrives, so a missing endpoint never flashes a bell.
//
// Idle rule: the session cookie is rolling, so a poll renews it. After IDLE_MS without input
// the bell stops polling, so an unattended desk still signs itself out; the next key or
// pointer press counts at once and resumes.
import { useEffect, useRef, useState } from 'react';

import { api } from '../../src/api/client.js';
import SlotBoundary from '../SlotBoundary.jsx';
import { IDLE_MS, badgeText, bellLabel, createUnreadPoller, fetchUnreadCount } from './bell-logic.js';
import { onNotificationsRefresh } from './refresh.js';

// The panel chunk, loaded on first open. Not React.lazy: lazy caches a failed import for the
// tab's life, so one failed load (a deploy swapped the chunk names) would kill the bell. A
// failure here clears the cache and the next click tries again.
let panelModule = null;
function loadPanel() {
  panelModule ??= import('./NotificationPanel.jsx').catch((err) => {
    panelModule = null;
    throw err;
  });
  return panelModule;
}

function BellIcon() {
  return (
    <svg viewBox="0 0 20 20" className="size-5" fill="currentColor" aria-hidden="true">
      <path d="M10 2a6 6 0 0 0-6 6v3.5L2.5 14v1h15v-1L16 11.5V8a6 6 0 0 0-6-6Zm-2 14a2 2 0 0 0 4 0Z" />
    </svg>
  );
}

const isVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

function useUnreadCount() {
  const [unread, setUnread] = useState(null); // null: no count yet, so nothing shows
  const [gone, setGone] = useState(false);
  const pollerRef = useRef(null);

  useEffect(() => {
    let lastInput = Date.now();
    const isActive = () => Date.now() - lastInput < IDLE_MS;
    const poller = createUnreadPoller({
      // The marker header keeps the poll from extending the session (bell-logic.js).
      fetchCount: (signal) => fetchUnreadCount((url, init) => window.fetch(url, init), signal),
      isVisible,
      isActive,
      schedule: (fn, ms) => window.setTimeout(fn, ms),
      cancel: (h) => window.clearTimeout(h),
      onCount: setUnread,
      onGone: () => setGone(true),
    });
    pollerRef.current = poller;
    const onVisibility = () => poller.visibilityChanged();
    const onFocus = () => poller.refresh();
    const onInput = () => {
      const wasIdle = !isActive();
      lastInput = Date.now();
      if (wasIdle) poller.activity();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pointerdown', onInput, { capture: true, passive: true });
    window.addEventListener('keydown', onInput, { capture: true, passive: true });
    const unsubscribe = onNotificationsRefresh(() => poller.refresh());
    poller.start();
    return () => {
      poller.stop();
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pointerdown', onInput, { capture: true });
      window.removeEventListener('keydown', onInput, { capture: true });
      pollerRef.current = null;
    };
  }, []);

  return { unread, gone, refresh: () => pollerRef.current?.refresh() };
}

export default function NotificationBell() {
  const { unread, gone, refresh } = useUnreadCount();
  const [open, setOpen] = useState(false);
  // The panel component once its chunk has loaded. It stays mounted (closed) after the
  // first open, so reopening is instant.
  const [Panel, setPanel] = useState(null);
  const buttonRef = useRef(null);

  if (gone || unread === null) return null;

  const label = bellLabel(unread);
  const badge = badgeText(unread);
  const setPanelOpen = (next) => {
    setOpen(next);
    if (!next) refresh();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (open) {
            setPanelOpen(false);
            return;
          }
          setOpen(true);
          if (!Panel) {
            loadPanel().then(
              (m) => setPanel(() => m.default),
              () => setOpen(false), // nothing to show; the next click retries the load
            );
          }
        }}
        className="relative grid size-9 place-items-center rounded-md text-ink-secondary
                   transition-colors hover:bg-sunken hover:text-ink
                   focus-visible:outline-2 focus-visible:outline-offset-2
                   focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
      >
        <BellIcon />
        {badge && (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-brand-600 px-1 text-center
                       text-[10px] font-semibold leading-4 text-white dark:bg-brand-500"
          >
            {badge}
          </span>
        )}
      </button>
      {Panel && (
        <SlotBoundary name="NotificationPanel">
          <Panel
            api={api}
            triggerRef={buttonRef}
            isOpen={open}
            onOpenChange={setPanelOpen}
            unread={unread}
            onChanged={refresh}
          />
        </SlotBoundary>
      )}
    </>
  );
}
